import { Injectable, inject, signal } from '@angular/core';

import { AppNotification } from '../models/notification.model';
import { NotificationService } from './notification.service';

const POLL_INTERVAL_MS = 90000;

@Injectable({
  providedIn: 'root'
})
export class NotificationStateService {
  private readonly api = inject(NotificationService);

  private readonly itemsSignal = signal<AppNotification[]>([]);
  private readonly unreadCountSignal = signal(0);
  private readonly loadingSignal = signal(false);

  readonly items = this.itemsSignal.asReadonly();
  readonly unreadCount = this.unreadCountSignal.asReadonly();
  readonly loading = this.loadingSignal.asReadonly();

  private initialized = false;
  private pollHandle: ReturnType<typeof setInterval> | null = null;

  /** Chamado no mount do topbar (sempre presente para usuário autenticado). Idempotente. */
  init(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    this.refreshUnreadCount();
    this.loadList();
    this.startPolling();

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.refreshUnreadCount();
        this.startPolling();
      } else {
        this.stopPolling();
      }
    });

    window.addEventListener('focus', () => this.refreshUnreadCount());
  }

  refreshUnreadCount(): void {
    this.api.unreadCount().subscribe({
      next: (count) => this.unreadCountSignal.set(count),
      error: () => {
        // Mantém último valor conhecido em caso de falha silenciosa.
      }
    });
  }

  loadList(page = 1, limit = 20): void {
    this.loadingSignal.set(true);
    this.api.list({ page, limit }).subscribe({
      next: (response) => {
        this.itemsSignal.set(response.items);
        this.loadingSignal.set(false);
      },
      error: () => this.loadingSignal.set(false)
    });
  }

  markRead(notificationPublicId: string): void {
    const wasUnread = this.itemsSignal().some((item) => item.public_id === notificationPublicId && !item.read_at);

    this.itemsSignal.update((items) =>
      items.map((item) => (item.public_id === notificationPublicId ? { ...item, read_at: item.read_at ?? new Date().toISOString() } : item))
    );
    if (wasUnread) {
      this.unreadCountSignal.update((count) => Math.max(0, count - 1));
    }

    this.api.markRead(notificationPublicId).subscribe({
      error: () => {
        this.loadList();
        this.refreshUnreadCount();
      }
    });
  }

  markAllRead(): void {
    const previousItems = this.itemsSignal();
    const previousCount = this.unreadCountSignal();

    this.itemsSignal.update((items) => items.map((item) => ({ ...item, read_at: item.read_at ?? new Date().toISOString() })));
    this.unreadCountSignal.set(0);

    this.api.markAllRead().subscribe({
      error: () => {
        this.itemsSignal.set(previousItems);
        this.unreadCountSignal.set(previousCount);
      }
    });
  }

  private startPolling(): void {
    if (this.pollHandle) {
      return;
    }
    this.pollHandle = setInterval(() => this.refreshUnreadCount(), POLL_INTERVAL_MS);
  }

  private stopPolling(): void {
    if (this.pollHandle) {
      clearInterval(this.pollHandle);
      this.pollHandle = null;
    }
  }
}
